import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { useState, useEffect, useRef } from 'react'

import { DefaultLayout } from '~/shared/layouts'
import Button from '~/components/Buttons/Button'
import { GEM_TYPE } from '~/components/QuestPanel/type'
import ArrowButton from '~/checklist/components/ArrowButton'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import ResponsiveImage from '~/components/Image/ResponsiveImage'
import { selectShouldShowTutorial } from '~/redux/slices/dashboard'
import { selectUserId } from '~/auth/slices/auth.slice'
import TutorialComponent from '~/dashboard/components/TutorialComponent'
import { REWARD_FEATURE } from '~/gamification/types/gamification.types'
import RewardsChest from '~/shared/components/RewardsChest/RewardsChest'
import { EventDetectionMethod, EventType } from '~/behavior-tracking/types'
import { logUserActivity } from '~/behavior-tracking/utility/view-event-logs.utility'
import { useGetRewardsByFeatureQuery, useSetRewardsMutation } from '~/redux/apis/reward-api'
import { randomNumbersToGenerateXpForPrerequisites } from '~/shared/sanity/utils/curriculum.utils'

import WarningModal from '~/components/WarningModal'

import {
  emptyLastLevelInfo,
  selectCurriculumSubjectContent,
  selectGradeContent,
  setCurriculumMappingQuizQuestionActionCreator,
  setCurriculumSubjectContentActionCreator,
  setLastLevelStatusInfoActionCreator,
  setShouldResetCurriculumMappingQuizActionCreator,
} from '../slice/curriculum.slice'
import PrerequisitesList from '../components/PrerequisitesList'
import CurriculumBackButton from '../components/organisms/BackButton'
import { CurriculumMappingQuizTask } from '../types/mapping-quiz.types'
import useMappingQuizPrerequisites from '../hooks/useMappingQuizPrerequisites'
import MQCurriculumMathTopicList from '../components/MQCurriculumMathTopicList'
import { useGetCurriculumProgressQuery } from '../apis/curriculum-progress.api'
import { useHandleCurriculumTutorial } from '../hooks/useHandleCurriculumTutorial'
import CurriculumMasteryTrack from '../components/templates/CurriculumMasteryTrack'
import { extractCountryCodeAndGradeIdFromRouterPath } from '../utils/extractQueryParams'
import { isMappingQuizApplicable, isMasteryTrackApplicable } from '../utils/validateCountryCodeGrade'

export default function SubjectCurriculumPage(): React.ReactElement {
  const dispatch = useAppDispatch()
  const router = useRouter()
  const { countryCode, gradeLevel, subjectOrder } = extractCountryCodeAndGradeIdFromRouterPath(router)
  const [prerequisites, setPrerequisites] = useState<CurriculumMappingQuizTask[] | []>([])
  const [isExpanded, setIsExpanded] = useState(true)
  const [currentProgress, setCurrentProgress] = useState<number>(0)

  const userId = useAppSelector(selectUserId)
  const gradeData = useAppSelector(selectGradeContent)
  const gradeId = `grade-${countryCode}-${gradeLevel}`
  const shouldShowTutorial = useAppSelector(selectShouldShowTutorial)
  const subject = useAppSelector(selectCurriculumSubjectContent)
  const selectSubjectPageUrl = `/curriculum/grade-${countryCode}/${gradeLevel}`
  const { tasks, totalPrerequisites, isQuizComplete, isQuizExist, isLoading } = useMappingQuizPrerequisites({
    countryCode,
    gradeLevel,
    subject,
  })
  const {
    data: curriculumProgressData,
    isLoading: progressLoading,
    isFetching: progressFetching,
  } = useGetCurriculumProgressQuery({ gradeData }, { skip: !userId })
  useEffect(() => {
    const subjectContent =
      (curriculumProgressData ?? gradeData).gradeSubdivisions
        ?.flatMap((subdivision) => subdivision.subjects)
        .find((sbj) => sbj.subjectOrder === Number(subjectOrder)) || null

    if (subjectContent) {
      const inCompleteLevelsCount = subjectContent.competencyGoals.reduce(
        (count, competency) => count + competency?.levels.filter((level) => !level.isCompleted).length,
        0,
      )
      if (inCompleteLevelsCount === 1) {
        const inCompleteLevelIds =
          subjectContent.competencyGoals.reduce((level, competency) => {
            const incompleteLevel = competency?.levels.find((l) => !l.isCompleted)
            return incompleteLevel ? incompleteLevel : level
          }, null)?.levelIds || []

        dispatch(
          setLastLevelStatusInfoActionCreator({
            isLastLevel: true,
            lastLevelIds: inCompleteLevelIds,
            gradeInfo: {
              countryCode,
              gradeId,
              gradeLevel: Number(gradeLevel),
              subjectOrder: subjectContent.subjectOrder,
              subject: subjectContent.subjectLabel,
              topicId: subjectContent.topicId,
            },
          }),
        )
      } else {
        dispatch(setLastLevelStatusInfoActionCreator(emptyLastLevelInfo))
      }
      dispatch(setCurriculumSubjectContentActionCreator({ ...subjectContent, subjectUrl: router.asPath }))
    }
  }, [countryCode, curriculumProgressData, dispatch, gradeData, gradeId, gradeLevel, router.asPath, subjectOrder])

  useEffect(() => {
    if (tasks && tasks?.length > 0) {
      if (curriculumProgressData?.progressIds && curriculumProgressData.progressIds.length > 0) {
        const { progressIds } = curriculumProgressData
        setCurrentProgress(0)
        const result = tasks?.map((item) => {
          const updatedSubjectList = item.subjectList?.map((subj) => {
            const updatedLevels = subj?.levels.map((level) => ({
              ...level,
              isCompleted: level.levelIds?.every((id) => progressIds?.includes(id)), // Check completion status for levels
            }))

            // Determine if the subjectList isCompleted based on levels' isCompleted
            const isSubjectCompleted = updatedLevels?.every((level) => level?.isCompleted)

            if (isSubjectCompleted) {
              setCurrentProgress((prev) => prev + 1)
            }
            return {
              ...subj,
              levels: updatedLevels,
              isCompleted: isSubjectCompleted, // Add isCompleted to subjectList
            }
          })

          // Determine if the topic node isCompleted based on subjectList's isCompleted
          const isTopicCompleted = updatedSubjectList?.every((su) => su?.isCompleted)
          return {
            subject: item.subject,
            order: item.order,
            subjectList: updatedSubjectList,
            isCompleted: isTopicCompleted, // Add isCompleted to parent node
          }
        })
        setPrerequisites(result)
      } else {
        setPrerequisites(tasks)
      }
    }
  }, [tasks, curriculumProgressData])

  const { t } = useTranslation('curriculum')

  const mappingQuizApplicable = isMappingQuizApplicable({ countryCode, grade: gradeLevel })
  const masteryTrackApplicable = isMasteryTrackApplicable({ countryCode })
  const totalLevels =
    prerequisites?.flatMap((task) => task.subjectList).reduce((total, sub) => total + sub?.levels.length, 0) ?? 0
  useHandleCurriculumTutorial({
    userId: userId ?? undefined,
    isQuizComplete,
    isLoading,
    mappingQuizApplicable,
    hasPrerequisite: (Number(totalPrerequisites) || 0) > 0,
  })

  const [totalRewards, setTotalRewards] = useState(0)
  useEffect(() => {
    if (totalLevels > 0) {
      setTotalRewards(Math.floor(randomNumbersToGenerateXpForPrerequisites[subjectOrder - 1] * totalLevels))
    }
  }, [totalLevels, subjectOrder])

  const rewardEventId = `curriculum#prerequisites#${gradeLevel}#${subjectOrder}`

  const { data: rewards, isLoading: isRewardsLoading } = useGetRewardsByFeatureQuery({ feature: 'curriculum' })
  const doesRewardExist = rewards?.some((e) => e.eventId === rewardEventId)
  const [setRewards] = useSetRewardsMutation()

  const [showResetMasteryTrackWarning, setShowResetMasteryTrackWarning] = useState(false)
  const [retakeQuizSource, setRetakeQuizSource] = useState<'from-must-knows' | 'from-mt-card'>('from-must-knows')
  const [rewardsChestOpen, setRewardsChestOpen] = useState(false)
  useEffect(() => {
    if (rewardsChestOpen && currentProgress === totalPrerequisites && !doesRewardExist && !isRewardsLoading) {
      setRewards({
        xp: totalRewards,
        kudos: 0,
        gems: 0,
        feature: REWARD_FEATURE.CURRICULUM,
        event_id: rewardEventId,
      })
    }
  }, [
    currentProgress,
    doesRewardExist,
    setRewards,
    totalPrerequisites,
    totalRewards,
    rewardEventId,
    isRewardsLoading,
    rewardsChestOpen,
  ])

  const handleGenerateNewPath = (source: 'from-must-knows' | 'from-mt-card' = 'from-must-knows'): void => {
    setRetakeQuizSource(source)
    if (totalPrerequisites && totalPrerequisites > 0) {
      setShowResetMasteryTrackWarning(true)
    } else {
      handleRetakeQuiz()
    }
  }

  const handleRetakeQuiz = (): void => {
    dispatch(
      setCurriculumMappingQuizQuestionActionCreator({
        questionId: '',
        quizId: '',
        title: '',
        description: [],
        preAskedQuesIds: [],
        isLastQuestion: false,
        isQuizComplete: false,
        tasks: [],
      }),
    )
    dispatch(setShouldResetCurriculumMappingQuizActionCreator(true))
    const url = `${router.asPath}/mapping-quiz`
    const eventType = retakeQuizSource === 'from-mt-card' ? 'retake-from-mt-card' : 'retake'
    logClickEvent(eventType)
    setShowResetMasteryTrackWarning(false)
    router.push(url)
  }

  const contentLoading = progressLoading || isLoading || progressFetching

  const rewardsRef = useRef<HTMLDivElement>(null)

  const logClickEvent = async (product: 'chest' | 'retake' | 'retake-from-mt-card' | 'start'): Promise<void> => {
    if (userId) {
      const { subjectLabel } = subject
      const subdivisionOrder = subject.competencyGoals[0].subdivisionOrder
      let eventType: EventDetectionMethod
      switch (product) {
        case 'chest':
          eventType = EventDetectionMethod.ON_CURRICULUM_CHEST_CLICK
          break
        case 'retake':
          eventType = EventDetectionMethod.ON_RETAKE_MAPPING_QUIZ_CLICK
          break
        case 'retake-from-mt-card':
          eventType = EventDetectionMethod.ON_RETAKE_MAPPING_QUIZ_FROM_MT_CARD_CLICK
          break
        case 'start':
          eventType = EventDetectionMethod.ON_START_MAPPING_QUIZ_CLICK
          break
      }

      const extra = {
        title: subjectLabel,
        countryCode,
        gradeLevel,
        subjectOrder,
        quarterNo: subdivisionOrder,
      }
      const event = {
        eventName: EventType.CURRICULUM_EVENT,
        viewKey: router.asPath,
        extra: {
          detectBy: eventType,
          ...extra,
        },
      }
      await logUserActivity(userId, event)
    }
  }

  return (
    <DefaultLayout
      dashboardGradientClass={gradeData.isJuniorMathGrade ? 'bg-gradient-blue-dark' : ''}
      disableScroll={shouldShowTutorial}
    >
      {showResetMasteryTrackWarning && (
        <WarningModal
          cancelOnClick={() => setShowResetMasteryTrackWarning(false)}
          continueOnClick={handleRetakeQuiz}
          i18nKey="curriculum:resetMasteryTrackWarning"
          i18nNamespace="curriculum"
        />
      )}
      {!isLoading && shouldShowTutorial && <TutorialComponent />}
      {/* background for junior grades */}
      {gradeData.isJuniorMathGrade && (
        <div className="top-4 left-0 fixed w-screen h-screen">
          <ResponsiveImage
            fit="cover"
            height={100}
            imageClassName="object-cover h-screen w-screen"
            srcKey="gamification/starry-backgound.svg"
            width={100}
          />
        </div>
      )}
      <div>
        <div className="flex">
          <CurriculumBackButton url={selectSubjectPageUrl} />
        </div>
        <div className="relative mx-auto lg:px-6 max-w-268 2xl:max-w-screen-2xl">
          <CurriculumMasteryTrack
            contentLoading={contentLoading}
            currentProgress={currentProgress}
            isQuizComplete={isQuizComplete}
            isQuizExist={isQuizExist}
            mappingQuizApplicable={mappingQuizApplicable}
            masteryTrackApplicable={masteryTrackApplicable}
            onClickGenerateNewPath={() => handleGenerateNewPath('from-mt-card')}
            subject={subject}
            title={gradeData.title}
            totalPrerequisites={totalPrerequisites || 0}
          />

          {totalPrerequisites && (
            <>
              <div className="relative flex flex-col justify-center items-center bg-purple-darker shadow-lg mx-auto mt-5 px-3 border border-white rounded-17 w-19/20 2xl:w-4/5">
                <div
                  className="flex justify-center items-center w-full h-20 cursor-pointer"
                  onClick={() => setIsExpanded(!isExpanded)}
                >
                  <div className="flex justify-start items-center gap-4 md:gap-6 mx-1 md:px-5 w-1/3 md:w-1/4">
                    <ResponsiveImage
                      alt="Puzzle Icon"
                      height={130}
                      imageClassName="w-10 md:w-15"
                      srcKey="onboarding/icons/no-space-puzzle-piece.svg"
                      width={130}
                    />
                    <span className="text-white md:text-8 tracking-widest">
                      {currentProgress}/{totalPrerequisites}
                    </span>
                  </div>
                  <h3 className="w-2/3 md:w-1/2 font-semibold text-white md:text-8 text-center md:leading-10 tracking-widest">
                    {t('mappingQuiz.stepOne')}
                  </h3>
                  <button className="z-10 flex justify-end w-1/3 md:w-1/4" onClick={() => setIsExpanded(!isExpanded)}>
                    <ArrowButton color="text-white" expanded={isExpanded} size={24} />
                  </button>
                </div>

                {(isExpanded || shouldShowTutorial) && (
                  <div className="w-full">
                    <div>
                      <h2 className="pt-4 pb-6 h-full md:font-light font-medium text-white lg:text-6 text-center tracking-widest">
                        {t('mappingQuiz.completeTopics')}
                      </h2>
                      {shouldShowTutorial && (
                        <div className="top-0 absolute opacity-0 border rounded-7 w-full h-50 md:h-52 curriculum-pop-up-tutorial-prerequisites" />
                      )}
                      <PrerequisitesList
                        disableScroll
                        grade={gradeLevel}
                        prerequisites={prerequisites ?? []}
                        topic={subject.subjectLabel}
                        userId={userId}
                        viewKey={router.asPath}
                      />
                      <div className="flex justify-center items-center w-full" ref={rewardsRef}>
                        {currentProgress === totalPrerequisites && rewardsChestOpen && !doesRewardExist && (
                          <div className="mb-15">
                            <RewardsChest
                              hasBackgroundBubble={false}
                              rewards={{ xp: totalRewards, kudos: 0, gemType: GEM_TYPE.RED, gemCount: 0 }}
                              rewardSize="medium"
                              size="small"
                              startAnimation
                            />
                          </div>
                        )}
                        {currentProgress === totalPrerequisites && doesRewardExist && (
                          <ResponsiveImage
                            alt="Treasure Chest"
                            height={100}
                            imageClassName="w-25 h-25"
                            srcKey="onboarding/icons/updated-chest-open.png"
                            width={95}
                          />
                        )}
                        {currentProgress === totalPrerequisites && !doesRewardExist && !rewardsChestOpen && (
                          <Button onClick={() => setRewardsChestOpen(true)}>
                            <ResponsiveImage
                              alt="Treasure Chest"
                              height={64}
                              imageClassName="w-30 h-22"
                              srcKey="onboarding/icons/updated-chest-purple.png"
                              width={86}
                            />
                          </Button>
                        )}
                        {currentProgress !== totalPrerequisites && (
                          <ResponsiveImage
                            alt="Treasure Chest"
                            height={64}
                            imageClassName="w-30 h-22 -mb-1 mt-4"
                            imageWidths="42, 64"
                            srcKey="curriculum/inactive-chest.svg"
                            width={86}
                          />
                        )}
                      </div>
                      <div className="flex justify-center md:justify-end items-center my-3 px-5 w-full">
                        <button
                          className="text-white underline"
                          onClick={() => handleGenerateNewPath('from-must-knows')}
                        >
                          {t('mappingQuiz.generateNewPath')}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
          {!contentLoading && (
            <MQCurriculumMathTopicList
              gradeId={gradeId}
              mappingQuizApplicable={mappingQuizApplicable}
              masteryTrackApplicable={masteryTrackApplicable}
              viewKey={router.asPath}
            />
          )}
        </div>
      </div>
    </DefaultLayout>
  )
}
